import React, { useEffect, useMemo, useState } from "react"
import { lumi } from "../lib/lumi"
import type { PointsSetting, SiteSetting, SiteSettingsTabKey } from "./siteSettingsTypes"
import { ConvenienceStorePickupTab, SiteSettingsMessage, SiteSettingsTabsBar } from "./SiteSettingsTabs"
import { SiteSettingsLoading, SiteSettingsPageTitle, SiteSettingsShell } from "./siteSettings/SiteSettingsManagerShell"
import SiteSettingsEmailTab from "./SiteSettingsEmailTab"
import SiteSettingsGenericTab from "./SiteSettingsGenericTab"
import SiteSettingsNewebpayTab from "./SiteSettingsNewebpayTab"
import SiteSettingsPointsTab from "./SiteSettingsPointsTab"

const SiteSettingsManager: React.FC = () => {
  const [settings, setSettings] = useState<SiteSetting[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null)
  const [activeTab, setActiveTab] = useState<SiteSettingsTabKey>("convenience_store_pickup")

  // 點數設定
  const [pointsSetting, setPointsSetting] = useState<PointsSetting | null>(null)
  const [pointsPerDollar, setPointsPerDollar] = useState(100)
  const [minPointsToUse, setMinPointsToUse] = useState(100)
  const [pointsIsActive, setPointsIsActive] = useState(true)
  const [pointsDescription, setPointsDescription] = useState("")

  // 超商取貨：ezShip SU ID
  const [ezshipSuId, setEzshipSuId] = useState("service@i-tea.com.tw")

  // 藍新金流
  const [newebpayMerchantId, setNewebpayMerchantId] = useState("")
  const [newebpayHashKey, setNewebpayHashKey] = useState("")
  const [newebpayHashIv, setNewebpayHashIv] = useState("")
  const [newebpayEnabled, setNewebpayEnabled] = useState(false)
  const [newebpayTestMode, setNewebpayTestMode] = useState(false)
  const [creditCardOnly, setCreditCardOnly] = useState(false)
  const [initializing, setInitializing] = useState(false)

  // 郵件系統
  const [emailEnabled, setEmailEnabled] = useState(true)
  const [resendApiKey, setResendApiKey] = useState("")

  const tabs = useMemo(
    () =>
      [
        { key: "convenience_store_pickup" as const, label: "超商取貨設定" },
        { key: "home_delivery" as const, label: "宅配服務設定" },
        { key: "store_pickup" as const, label: "門市自取設定" },
        { key: "points_system" as const, label: "會員積分系統設定" },
        { key: "coupon_system" as const, label: "優惠券系統設定" },
        { key: "newebpay_settings" as const, label: "藍新金流設定" },
        { key: "email_system" as const, label: "郵件系統設定" }
      ],
    []
  )

  const showMessage = (type: "success" | "error", text: string) => {
    setMessage({ type, text })
    setTimeout(() => setMessage(null), 5000)
  }

  const fetchSettings = async () => {
    try {
      setLoading(true)
      const { list } = await lumi.entities.site_settings.list({ sort: { order: 1 } })

      // 過濾掉貨到付款設定
      const filteredSettings = (list || []).filter((s: SiteSetting) => s.settingKey !== "cash_on_delivery")
      setSettings(filteredSettings)

      const convenienceStoreSetting = filteredSettings.find((s) => s.settingKey === "convenience_store_pickup") as any
      if (convenienceStoreSetting?.ezshipSuId) {
        setEzshipSuId(convenienceStoreSetting.ezshipSuId)
      }

      const newebpaySetting = filteredSettings.find((s) => s.settingKey === "newebpay_settings") as any
      if (newebpaySetting) {
        setNewebpayEnabled(newebpaySetting.enabled || false)
        setNewebpayMerchantId(newebpaySetting.newebpayMerchantId || "")
        setNewebpayHashKey(newebpaySetting.newebpayHashKey || "")
        setNewebpayHashIv(newebpaySetting.newebpayHashIv || "")
        setNewebpayTestMode(newebpaySetting.newebpayTestMode || false)
        setCreditCardOnly(newebpaySetting.creditCardOnly || false)
      }

      const emailSetting = filteredSettings.find((s) => s.settingKey === "email_system") as any
      if (emailSetting) {
        setEmailEnabled(emailSetting.enabled ?? true)
        setResendApiKey(emailSetting.resendApiKey || "")
      }
    } catch (error) {
      console.error("❌ 加載網站設定失敗:", error)
      showMessage("error", "加載設定失敗，請重新整理頁面")
    } finally {
      setLoading(false)
    }
  }

  const fetchPointsSettings = async () => {
    try {
      const { list } = await lumi.entities.points_settings.list({ filter: { settingKey: "default" } })
      if (list && list.length > 0) {
        const currentSetting = list[0]
        setPointsSetting(currentSetting)
        setPointsPerDollar(currentSetting.pointsPerDollar)
        setMinPointsToUse(currentSetting.minPointsToUse)
        setPointsIsActive(currentSetting.isActive)
        setPointsDescription(currentSetting.description || "")
      }
    } catch (error) {
      console.error("Failed to fetch points settings:", error)
    }
  }

  useEffect(() => {
    void fetchSettings()
    void fetchPointsSettings()
  }, [])

  const toggleSetting = (id: string) => {
    setSettings((prev) => prev.map((s) => (s._id === id ? { ...s, enabled: !s.enabled } : s)))
  }

  const currentSetting = settings.find((s) => s.settingKey === activeTab)

  const handleInitNewebpaySettings = async () => {
    try {
      setInitializing(true)
      setMessage(null)

      const response: any = await lumi.functions.invoke("initNewebpaySettings", { method: "POST" })
      if (response?.success) {
        showMessage("success", response.message || "藍新金流設定已成功初始化")
        await fetchSettings()
      } else {
        showMessage("error", response?.error || "初始化失敗")
      }
    } catch (error: any) {
      console.error("❌ 初始化藍新金流設定失敗:", error)
      showMessage("error", error?.data?.error || "初始化失敗，請稍後再試")
    } finally {
      setInitializing(false)
    }
  }

  const handleSaveCurrentTab = async () => {
    try {
      setSaving(true)
      setMessage(null)

      if (activeTab === "points_system") {
        if (pointsPerDollar <= 0) {
          showMessage("error", "點數兌換比例必須大於0")
          return
        }
        if (minPointsToUse < 0) {
          showMessage("error", "最低使用點數不能為負數")
          return
        }

        const now = new Date().toISOString()
        const data = {
          pointsPerDollar,
          minPointsToUse,
          isActive: pointsIsActive,
          description: pointsDescription.trim() || `點數兌換規則：${pointsPerDollar}分=1元，最低使用${minPointsToUse}分`,
          updatedAt: now
        }

        if (pointsSetting) {
          await lumi.entities.points_settings.update(pointsSetting._id, data)
        } else {
          await lumi.entities.points_settings.create({ ...data, settingKey: "default", createdAt: now })
        }

        await fetchPointsSettings()
        showMessage("success", "點數設定已成功保存")

        // 同步更新 site_settings 的 points_system 開關
        const pointsSystemSetting = settings.find((s) => s.settingKey === activeTab)
        if (pointsSystemSetting) {
          await lumi.entities.site_settings.update(pointsSystemSetting._id, { enabled: pointsIsActive, updatedAt: now })
          await fetchSettings()
        }

        return
      }

      if (activeTab === "convenience_store_pickup") {
        const s = settings.find((x) => x.settingKey === activeTab)
        if (!s) return
        if (!ezshipSuId.trim()) {
          showMessage("error", "ezShip 商家账号不能为空")
          return
        }
        await lumi.entities.site_settings.update(s._id, {
          enabled: s.enabled,
          ezshipSuId: ezshipSuId.trim(),
          updatedAt: new Date().toISOString()
        })
        showMessage("success", "設定已成功保存")
        await fetchSettings()
        return
      }

      if (activeTab === "newebpay_settings") {
        if (newebpayEnabled && (!newebpayMerchantId.trim() || !newebpayHashKey.trim() || !newebpayHashIv.trim())) {
          showMessage("error", "請完整填寫藍新金流商家資訊（商店代號、HashKey、HashIV）")
          return
        }

        const now = new Date().toISOString()
        const settingData: any = {
          enabled: newebpayEnabled,
          newebpayMerchantId: newebpayMerchantId.trim(),
          newebpayHashKey: newebpayHashKey.trim(),
          newebpayHashIv: newebpayHashIv.trim(),
          newebpayTestMode,
          creditCardOnly,
          updatedAt: now
        }

        if (currentSetting) {
          await lumi.entities.site_settings.update(currentSetting._id, settingData)
        } else {
          await lumi.entities.site_settings.create({
            ...settingData,
            settingKey: "newebpay_settings",
            displayName: "藍新金流設定",
            description: "設定藍新金流支付服務的商家資訊",
            order: 6,
            createdAt: now
          })
        }

        showMessage("success", "設定已成功保存")
        await fetchSettings()
        return
      }

      if (activeTab === "email_system") {
        const now = new Date().toISOString()
        const settingData: any = { enabled: emailEnabled, resendApiKey: resendApiKey.trim(), updatedAt: now }

        if (currentSetting) {
          await lumi.entities.site_settings.update(currentSetting._id, settingData)
        } else {
          await lumi.entities.site_settings.create({
            ...settingData,
            settingKey: "email_system",
            displayName: "郵件系統設定",
            description: "控制網站是否發送 Email 通知（訂單確認、歡迎郵件等）",
            order: 7,
            createdAt: now
          })
        }

        showMessage("success", "設定已成功保存")
        await fetchSettings()
        return
      }

      // 其他 tab：只保存 enabled
      if (!currentSetting) return
      await lumi.entities.site_settings.update(currentSetting._id, {
        enabled: currentSetting.enabled,
        updatedAt: new Date().toISOString()
      })
      showMessage("success", "設定已成功保存")
      await fetchSettings()
    } catch (error) {
      console.error("❌ 保存設定失敗:", error)
      showMessage("error", "保存失敗，請稍後再試")
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <SiteSettingsLoading />
  }

  const saveDisabled = !currentSetting && activeTab !== "points_system" && activeTab !== "newebpay_settings" && activeTab !== "email_system"

  return (
    <SiteSettingsShell>
      <SiteSettingsPageTitle />

      <SiteSettingsTabsBar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onSave={handleSaveCurrentTab}
        saving={saving}
        saveDisabled={saveDisabled}
      />

      <SiteSettingsMessage message={message} />

      {activeTab === "points_system" ? (
        <SiteSettingsPointsTab
          pointsSetting={pointsSetting}
          pointsPerDollar={pointsPerDollar}
          minPointsToUse={minPointsToUse}
          pointsIsActive={pointsIsActive}
          pointsDescription={pointsDescription}
          setPointsPerDollar={setPointsPerDollar}
          setMinPointsToUse={setMinPointsToUse}
          setPointsIsActive={setPointsIsActive}
          setPointsDescription={setPointsDescription}
        />
      ) : activeTab === "convenience_store_pickup" ? (
        <ConvenienceStorePickupTab
          setting={currentSetting}
          ezshipSuId={ezshipSuId}
          onToggle={toggleSetting}
          onChangeEzshipSuId={setEzshipSuId}
        />
      ) : activeTab === "newebpay_settings" ? (
        <SiteSettingsNewebpayTab
          newebpayEnabled={newebpayEnabled}
          setNewebpayEnabled={setNewebpayEnabled}
          newebpayMerchantId={newebpayMerchantId}
          setNewebpayMerchantId={setNewebpayMerchantId}
          newebpayHashKey={newebpayHashKey}
          setNewebpayHashKey={setNewebpayHashKey}
          newebpayHashIv={newebpayHashIv}
          setNewebpayHashIv={setNewebpayHashIv}
          newebpayTestMode={newebpayTestMode}
          setNewebpayTestMode={setNewebpayTestMode}
          creditCardOnly={creditCardOnly}
          setCreditCardOnly={setCreditCardOnly}
          initializing={initializing}
          onInitFromEnv={handleInitNewebpaySettings}
          currentSetting={currentSetting}
        />
      ) : activeTab === "email_system" ? (
        <SiteSettingsEmailTab
          emailEnabled={emailEnabled}
          setEmailEnabled={setEmailEnabled}
          resendApiKey={resendApiKey}
          setResendApiKey={setResendApiKey}
        />
      ) : (
        <SiteSettingsGenericTab setting={currentSetting} onToggle={toggleSetting} />
      )}
    </SiteSettingsShell>
  )
}

export default SiteSettingsManager
